/**
 * posModeSwitch.js
 *
 * Pure decision logic for switching between the POS selling modes (quick receipt,
 * tables, delivery) and the POS Hub. Extracted from CircularButton so the order-type
 * button and the Hub tiles apply EXACTLY the same cart / table reset rules, and so
 * the rules can be unit-tested without React or navigation.
 */

/** Stack route per mode. */
export const POS_MODE_ROUTES = Object.freeze({
    takeAway: 'HomeScreen',
    dineIn: 'TablesScreen',
    delivery: 'OnlineScreen',
    hub: 'PosHubScreen',
});

/**
 * Decides what a mode switch must do.
 *
 * Result shape:
 *  - `confirmDeliveryDraft`: a delivery draft (customer + address) exists, ask first
 *    (`home_delivery_alert`); only then continue with `action`.
 *  - `action.type === 'none'`            nothing to do (already there).
 *  - `action.type === 'goto'`            navigate to `action.screen`; `keepState: true`
 *                                        keeps the cart / table (resuming), otherwise a
 *                                        non-empty cart asks for confirmation and the
 *                                        selling state is reset.
 *  - `action.type === 'confirmQuickReceipt'` reset the selling state and open a fresh quick
 *                                        receipt after confirming with `action.messageKey`.
 *
 * @param {object} args
 * @param {('takeAway'|'dineIn'|'delivery'|'hub')} args.mode Requested mode.
 * @param {string} args.currentRoute Name of the host route the user is on.
 * @param {boolean} args.hasDineInSelection A table (or dine-in draft) is selected.
 * @param {boolean} args.hasItemsInProgress Cart or already-sent items exist.
 * @param {boolean} args.hasDeliveryDraft Delivery draft with customer + address exists.
 * @returns {{confirmDeliveryDraft: boolean, action: {type: string, screen?: string, keepState?: boolean, messageKey?: string}}}
 */
export const planPosModeSwitch = ({mode, currentRoute, hasDineInSelection, hasItemsInProgress, hasDeliveryDraft}) => {
    const none = {type: 'none'};
    let action = none;

    if (mode === 'hub') {
        // Peeking at the Hub never discards work in progress (the Hub offers "Resume").
        action = currentRoute === POS_MODE_ROUTES.hub ? none : {type: 'goto', screen: POS_MODE_ROUTES.hub, keepState: true};
    } else if (mode === 'takeAway') {
        if (hasDineInSelection) {
            action = {type: 'confirmQuickReceipt', messageKey: 'switch_to_quick_receipt_confirm'};
        } else if (currentRoute === POS_MODE_ROUTES.hub) {
            // From the Hub a quick receipt in progress is resumed, not deleted.
            action = {type: 'goto', screen: POS_MODE_ROUTES.takeAway, keepState: true};
        } else if (currentRoute === POS_MODE_ROUTES.takeAway && hasItemsInProgress) {
            action = {type: 'confirmQuickReceipt', messageKey: 'delete_cart_confirm'};
        } else if (currentRoute !== POS_MODE_ROUTES.takeAway) {
            action = {type: 'goto', screen: POS_MODE_ROUTES.takeAway, keepState: false};
        }
    } else if (mode === 'dineIn' || mode === 'delivery') {
        const screen = POS_MODE_ROUTES[mode];

        if (currentRoute !== screen) {
            action = {type: 'goto', screen, keepState: false};
        }
    }

    return {confirmDeliveryDraft: !!hasDeliveryDraft, action};
};
